import { ArrowUpRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-32 pb-28 md:pt-44 md:pb-36 overflow-hidden"
    >
      {/* Background layers */}
      <div className="absolute inset-0 bg-grid opacity-60 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
      <div className="absolute inset-0 bg-radial-glow" />
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[680px] h-[680px] rounded-full bg-blue-500/10 blur-[140px]" />

      <div className="relative max-w-5xl mx-auto px-6 text-center">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur reveal-up" style={{ animationDelay: "0.05s" }}>
          <Sparkles size={13} className="text-blue-400" />
          <span className="text-[11px] tracking-[0.22em] uppercase text-white/70">
            Premium Web Design For Local Businesses
          </span>
        </div>

        <h1
          data-testid="hero-headline"
          className="font-display text-4xl sm:text-5xl lg:text-7xl tracking-tight font-semibold leading-[1.05] mt-7 reveal-up"
          style={{ animationDelay: "0.15s" }}
        >
          Modern Websites That Help
          <br className="hidden md:block" />{" "}
          <span className="italic font-normal text-white/95">Local Businesses</span>{" "}
          Get{" "}
          <span className="relative inline-block">
            <span className="relative z-10">More Leads</span>
            <span className="absolute left-0 right-0 -bottom-1 h-3 bg-blue-500/40 -z-0 blur-[2px]" />
          </span>
        </h1>

        <p
          data-testid="hero-subheadline"
          className="mt-7 text-base md:text-lg text-white/65 max-w-2xl mx-auto leading-relaxed reveal-up"
          style={{ animationDelay: "0.3s" }}
        >
          I build clean, high-converting websites, landing pages, and redesign
          concepts for gyms, detailers, roofers, service businesses, and local
          brands that want to look more professional online.
        </p>

        <div
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 reveal-up"
          style={{ animationDelay: "0.45s" }}
        >
          <Button
            data-testid="hero-cta-primary"
            onClick={() => scrollTo("contact")}
            className="group bg-blue-500 hover:bg-blue-600 text-white rounded-full h-12 px-7 text-sm font-medium shadow-[0_10px_40px_-10px_rgba(59,130,246,0.6)]"
          >
            Get a Free Website Review
            <ArrowUpRight
              size={16}
              className="ml-1.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </Button>
          <Button
            data-testid="hero-cta-secondary"
            onClick={() => scrollTo("work")}
            variant="outline"
            className="bg-transparent border-white/20 hover:bg-white/5 hover:border-white/40 text-white rounded-full h-12 px-7 text-sm font-medium"
          >
            View My Work
          </Button>
        </div>

        <div
          data-testid="hero-trust-line"
          className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-white/45 reveal-up"
          style={{ animationDelay: "0.6s" }}
        >
          <span className="flex items-center gap-2">
            <span className="w-1 h-1 rounded-full bg-blue-400" />
            Fast turnaround
          </span>
          <span className="flex items-center gap-2">
            <span className="w-1 h-1 rounded-full bg-blue-400" />
            Mobile-friendly
          </span>
          <span className="flex items-center gap-2">
            <span className="w-1 h-1 rounded-full bg-blue-400" />
            Built for real business results
          </span>
        </div>
      </div>

      {/* Honest credibility strip (no fake numbers) */}
      <div className="relative max-w-5xl mx-auto px-6 mt-20 md:mt-28">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
          {[
            { k: "Mobile-first", v: "Every site is built for phones first." },
            { k: "Clean design", v: "Professional, trustworthy, easy to read." },
            { k: "Direct contact", v: "You talk to me, not a sales team." },
            { k: "Lead-focused", v: "Built to turn visitors into customers." },
          ].map((s) => (
            <div
              key={s.k}
              data-testid={`hero-feature-${s.k.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              className="bg-[#0A0A0A] p-6 md:p-7"
            >
              <div className="font-display text-xl md:text-2xl text-white">{s.k}</div>
              <div className="text-xs md:text-sm text-white/50 mt-1.5 leading-relaxed">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
